import { tv } from "@ui/lib/tv";
import { composeRenderProps } from "@ui/primitives/shared";
import {
  ToggleButton as RACToggleButton,
  type ToggleButtonProps as RACToggleButtonProps,
} from "@ui/primitives/ToggleButton";
import {
  ToggleButtonGroup as RACToggleButtonGroup,
  type ToggleButtonGroupProps as RACToggleButtonGroupProps,
  SelectionIndicator,
} from "@ui/primitives/ToggleButtonGroup";

const toggleChip = tv({
  slots: {
    group: "grid gap-1 rounded-[16px] p-1 shadow-keyline-inset",
    chip: [
      "relative flex cursor-pointer items-center justify-center rounded-[12px] py-2 outline-none whitespace-nowrap",
      "text-neutral-900",
      "selected:font-semibold selected:text-red-400",
      "transition-colors",
      "focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-2",
    ],
    // Stacks chip content (e.g. an icon above its label) above the sliding indicator.
    content: "relative z-10 flex flex-col items-center justify-center gap-0.5",
    indicator: [
      "absolute inset-0 rounded-[12px] border-2 border-red-400 bg-red-50",
      "motion-safe:transition-[translate,width,height] motion-safe:duration-200",
    ],
  },
});

export interface ToggleChipGroupProps extends RACToggleButtonGroupProps {}

/**
 * A grid of selectable chips inside an inset-keyline well — the multi-select
 * filter surface (climate, timezone, …). Pass the column count via
 * `className` (e.g. `grid-cols-5`); render `ToggleChip` children.
 */
export function ToggleChipGroup(props: ToggleChipGroupProps) {
  const styles = toggleChip();

  return (
    <RACToggleButtonGroup
      {...props}
      className={composeRenderProps(props.className, (className) => styles.group({ className }))}
    />
  );
}

export interface ToggleChipProps extends RACToggleButtonProps {}

/**
 * One chip inside a `ToggleChipGroup`: neutral text that turns semibold red
 * when selected, with the group's shared sliding red indicator behind it.
 * Content stacks vertically, so an icon above a label works out of the box.
 */
export function ToggleChip(props: ToggleChipProps) {
  const styles = toggleChip();

  return (
    <RACToggleButton
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        styles.chip({ ...renderProps, className }),
      )}
    >
      {composeRenderProps(props.children, (children) => (
        <>
          <SelectionIndicator className={styles.indicator()} />
          <span className={styles.content()}>{children}</span>
        </>
      ))}
    </RACToggleButton>
  );
}
